import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { calendarV2Api } from "./api";
import "./CalendarV2.css";

const DAY_START = 7 * 60 + 45;
const DAY_END = 19 * 60 + 45;
const TOTAL_MINUTES = DAY_END - DAY_START;
const LONG_PRESS_MS = 320;
const MOVE_CANCEL_PX = 9;
const SNAP_MINUTES = 5;

const pad = (value) => String(value).padStart(2, "0");
const toMinutes = (value) => {
  const [hour, minute] = value.split(":").map(Number);
  return hour * 60 + minute;
};
const toTime = (minutes) => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
const iso = (value) => `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
const londonClock = (value = new Date()) => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(value).map((part) => [part.type, part.value]),
  );
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    minutes: Number(parts.hour) * 60 + Number(parts.minute),
  };
};
const fromIso = (value) => new Date(`${value}T12:00:00`);
const addDays = (value, amount) => {
  const next = new Date(value);
  next.setDate(next.getDate() + amount);
  return next;
};
const formatTime = (value) => {
  const [hour, minute] = value.split(":").map(Number);
  const suffix = hour >= 12 ? "pm" : "am";
  const twelve = hour % 12 || 12;
  return minute ? `${twelve}:${pad(minute)}${suffix}` : `${twelve}${suffix}`;
};
const formatDay = (value) => fromIso(value).toLocaleDateString(undefined, {
  weekday: "short", day: "numeric", month: "short",
});

function applyTemporalTruth(day, now = new Date()) {
  const london = londonClock(now);
  const today = london.date;
  const nowMinutes = london.minutes;
  return {
    ...day,
    spaces: day.spaces.map((space) => {
      const hasEnded =
        day.date < today ||
        (day.date === today && toMinutes(space.end) <= nowMinutes);
      return hasEnded && space.state !== "elapsed"
        ? { ...space, state: "elapsed" }
        : space;
    }),
  };
}

function normaliseDay(payload, date) {
  return applyTemporalTruth({
    date,
    working: payload?.working !== false,
    spaces: (payload?.spaces || []).map((space, index) => ({
      ...space,
      key: space.booking_id || `${date}-${space.start}-${index}`,
    })),
  });
}

function CalendarSpace({
  space,
  selected,
  onSelect,
  onAssign,
  onOpenBooking,
  onChange,
  gridRef,
}) {
  const drag = useRef(null);
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState("");
  const current = preview || space;
  const start = toMinutes(current.start);
  const end = toMinutes(current.end);
  const top = ((start - DAY_START) / TOTAL_MINUTES) * 100;
  const height = ((end - start) / TOTAL_MINUTES) * 100;
  const available = space.state === "available";
  const occupied = !available && Boolean(
    space.booking_id || space.pupil_id || space.pupil_user_id || space.student_id || space.pupil_name
  );
  const interactive = available;

  const clearTimer = () => {
    if (drag.current?.timer) window.clearTimeout(drag.current.timer);
  };

  const begin = (event, mode) => {
    if (!interactive || event.pointerType === "mouse" && event.button !== 0) return;
    event.stopPropagation();
    const node = event.currentTarget;
    const gridHeight = gridRef.current?.getBoundingClientRect().height || 780;
    drag.current = {
      mode,
      pointerId: event.pointerId,
      node,
      startY: event.clientY,
      originalStart: toMinutes(space.start),
      originalEnd: toMinutes(space.end),
      pxPerMinute: gridHeight / TOTAL_MINUTES,
      active: mode !== "move",
      timer: null,
      preview: null,
    };
    if (mode === "move") {
      drag.current.timer = window.setTimeout(() => {
        if (!drag.current) return;
        drag.current.active = true;
        drag.current.node.setPointerCapture?.(drag.current.pointerId);
        setMessage("Move active · release to place");
      }, LONG_PRESS_MS);
    } else {
      node.setPointerCapture?.(event.pointerId);
      setMessage(mode === "start" ? "Resizing start" : "Resizing end");
    }
  };

  const move = (event) => {
    const state = drag.current;
    if (!state || state.pointerId !== event.pointerId) return;
    const distance = event.clientY - state.startY;
    if (!state.active) {
      if (Math.abs(distance) > MOVE_CANCEL_PX) {
        clearTimer();
        drag.current = null;
      }
      return;
    }
    event.preventDefault();
    const delta = Math.round(distance / state.pxPerMinute / SNAP_MINUTES) * SNAP_MINUTES;
    let nextStart = state.originalStart;
    let nextEnd = state.originalEnd;
    if (state.mode === "move") {
      const duration = state.originalEnd - state.originalStart;
      nextStart = Math.max(DAY_START, Math.min(DAY_END - duration, state.originalStart + delta));
      nextEnd = nextStart + duration;
    } else if (state.mode === "start") {
      nextStart = Math.max(DAY_START, Math.min(state.originalEnd - 30, state.originalStart + delta));
    } else {
      nextEnd = Math.min(DAY_END, Math.max(state.originalStart + 30, state.originalEnd + delta));
    }
    const nextPreview = { ...space, start: toTime(nextStart), end: toTime(nextEnd) };
    state.preview = nextPreview;
    setPreview(nextPreview);
    setMessage(`${formatTime(toTime(nextStart))}–${formatTime(toTime(nextEnd))}`);
  };

  const finish = (event) => {
    const state = drag.current;
    if (!state || state.pointerId !== event.pointerId) return;
    clearTimer();
    const finalPreview = state.preview || preview;
    if (state.active && finalPreview) {
      onChange(space, finalPreview);
    }
    window.setTimeout(() => {
      drag.current = null;
      setPreview(null);
      setMessage("");
    }, 0);
  };

  const open = () => {
    if (drag.current?.active) return;
    onSelect(space);
    if (available) onAssign?.(space);
    if (occupied) onOpenBooking?.(space);
  };

  return (
    <article
      className={`cv2-slot cv2-slot--${space.state} ${selected ? "is-selected" : ""}`}
      style={{ top: `${top}%`, height: `${Math.max(height, 7.4)}%` }}
      data-state={space.state}
    >
      <button
        type="button"
        className="cv2-slot__body"
        aria-label={`${space.state} ${formatTime(current.start)} to ${formatTime(current.end)}`}
        onClick={open}
        onPointerDown={(event) => begin(event, "move")}
        onPointerMove={move}
        onPointerUp={finish}
        onPointerCancel={finish}
      >
        <img className="cv2-slot__car" src="/assets/ndg-calendar-car-b.png" alt="" draggable="false" />
        <span className="cv2-slot__glass">
          <strong>{space.state === "available" ? "Available" : space.state === "elapsed" ? (space.pupil_name ? `Past · ${space.pupil_name}` : "Past") : space.pupil_name || "Booked"}</strong>
          <span>{formatTime(current.start)}–{formatTime(current.end)}</span>
        </span>
        {space.state === "available" && <span className="cv2-slot__action">Assign</span>}
      </button>
      {interactive && (
        <>
          <button
            type="button"
            className="cv2-slot__grip cv2-slot__grip--start"
            aria-label="Resize start time"
            onPointerDown={(event) => begin(event, "start")}
            onPointerMove={move}
            onPointerUp={finish}
            onPointerCancel={finish}
          />
          <button
            type="button"
            className="cv2-slot__grip cv2-slot__grip--end"
            aria-label="Resize end time"
            onPointerDown={(event) => begin(event, "end")}
            onPointerMove={move}
            onPointerUp={finish}
            onPointerCancel={finish}
          />
        </>
      )}
      {message && <output className="cv2-slot__preview">{message}</output>}
    </article>
  );
}

function DayTimeline({ day, selected, setSelected, onAssign, onOpenBooking, onSlotChange }) {
  const gridRef = useRef(null);
  const hours = Array.from({ length: 14 }, (_, index) => index + 7);
  if (!day.working) {
    return <div className="cv2-day-off"><span>Day off</span><p>No booking spaces are open.</p></div>;
  }
  return (
    <div className="cv2-timeline" aria-label={`Calendar for ${formatDay(day.date)}`}>
      <div className="cv2-timeline__scroll">
        <div className="cv2-timeline__grid" ref={gridRef}>
          {hours.map((hour) => (
            <div className="cv2-hour" key={hour} style={{ top: `${((hour * 60 - DAY_START) / TOTAL_MINUTES) * 100}%` }}>
              <time>{formatTime(`${pad(hour)}:00`)}</time>
              <span />
            </div>
          ))}
          {day.spaces.map((space) => (
            <CalendarSpace
              key={space.key}
              space={space}
              selected={selected?.key === space.key}
              onSelect={setSelected}
              onAssign={onAssign}
              onOpenBooking={onOpenBooking}
              onChange={onSlotChange}
              gridRef={gridRef}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function WeekView({ days, onChoose }) {
  return (
    <div className="cv2-week">
      {days.map((day) => (
        <button type="button" key={day.date} className="cv2-week__day" onClick={() => onChoose(day.date)}>
          <span><strong>{formatDay(day.date)}</strong><small>{day.working ? `${day.spaces.length} spaces` : "Day off"}</small></span>
          <span className="cv2-week__summary" aria-hidden="true">{day.spaces.length} spaces</span>
        </button>
      ))}
    </div>
  );
}

function MonthView({ days, onChoose }) {
  return (
    <div className="cv2-month">
      {days.map((day) => {
        const booked = day.spaces.filter((space) => space.state === "booked").length;
        const available = day.spaces.filter((space) => space.state === "available").length;
        return (
          <button type="button" key={day.date} onClick={() => onChoose(day.date)}>
            <time>{formatDay(day.date)}</time>
            <span>{day.working ? `${booked} booked · ${available} available` : "Day off"}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function CalendarV2({
  initialDate = londonClock().date,
  dataSource = calendarV2Api,
  initialDay = null,
  onAssign,
  onOpenBooking,
  onRepeat,
  onSlotChange,
}) {
  const [date, setDate] = useState(initialDate);
  const [mode, setMode] = useState("day");
  const [days, setDays] = useState(initialDay ? [normaliseDay(initialDay, initialDate)] : []);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState(initialDay ? "ready" : "loading");
  const [error, setError] = useState("");

  // Move visible slots through time without requiring a refresh. The backend
  // remains authoritative; this clock tick keeps the mounted day truthful at
  // the exact lesson boundary.
  useEffect(() => {
    const advanceClock = () => setDays((current) => current.map(
      (day) => applyTemporalTruth(day),
    ));
    const timer = window.setInterval(advanceClock, 15000);
    return () => window.clearInterval(timer);
  }, []);

  const span = mode === "day" ? 1 : mode === "week" ? 7 : 28;
  const requestedDates = useMemo(
    () => Array.from({ length: span }, (_, index) => iso(addDays(fromIso(date), index))),
    [date, span],
  );

  const load = useCallback(async () => {
    if (initialDay && mode === "day" && date === initialDate) {
      setDays([normaliseDay(initialDay, date)]);
      setStatus("ready");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      const loaded = await Promise.all(requestedDates.map(async (item) => normaliseDay(await dataSource.day(item), item)));
      setDays(loaded);
      setStatus("ready");
    } catch (reason) {
      setError(reason.message || "Calendar could not be loaded");
      setStatus("error");
    }
  }, [dataSource, date, initialDate, initialDay, mode, requestedDates]);

  useEffect(() => { load(); }, [load]);

  const chooseDay = (next) => {
    setDate(next);
    setMode("day");
    setSelected(null);
  };

  const moveDate = (amount) => {
    setDate(iso(addDays(fromIso(date), amount)));
    setSelected(null);
  };

  const updateSlot = async (original, changed) => {
    const replace = (replacement) => setDays((current) => current.map((day) => ({
      ...day,
      spaces: day.spaces.map((space) => (
        space.key === original.key ? replacement : space
      )),
    })));
    replace(changed);
    try {
      const result = await dataSource.changeSpace({ date, original, changed });
      onSlotChange?.({ date, original, changed, result });
    } catch (reason) {
      replace(original);
      onSlotChange?.({
        date,
        original,
        changed,
        error: reason.message || "That change could not be saved",
      });
    }
  };

  return (
    <section className="cv2" data-testid="calendar-v2">
      <div className="cv2-compactbar">
        <button type="button" onClick={() => moveDate(-1)} aria-label="Previous day">‹</button>
        <strong>{formatDay(date)}</strong>
        <button type="button" className="cv2-today" onClick={() => chooseDay(londonClock().date)}>Today</button>
        <button type="button" onClick={() => moveDate(1)} aria-label="Next day">›</button>
      </div>

      {status === "loading" && <div className="cv2-state">Loading your diary…</div>}
      {status === "error" && <div className="cv2-state cv2-state--error"><strong>Calendar unavailable</strong><span>{error}</span><button type="button" onClick={load}>Try again</button></div>}
      {status === "ready" && mode === "day" && days[0] && (
        <DayTimeline
          day={days[0]}
          selected={selected}
          setSelected={setSelected}
          onAssign={(space) => onAssign?.({ date, space })}
          onOpenBooking={(space) => onOpenBooking?.({ date, space })}
          onSlotChange={updateSlot}
        />
      )}
      {status === "ready" && mode === "week" && <WeekView days={days} onChoose={chooseDay} />}
      {status === "ready" && mode === "month" && <MonthView days={days} onChoose={chooseDay} />}
    </section>
  );
}
